/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { HC_THEME_POLLING_INTERVAL } from "constants/constants";
import { hcThemeColors } from "constants/enums";
import { trackHCEnabled, trackHCDisabled, trackHCThemeChange } from "analytics/events";
import MediaQueryProvider from "dc-preview-utils/src/components/withMediaQuery/MediaQueryProvider";

const MEDIA_QUERY_FORCED_COLORS = "FORCED_COLORS";
const FC_ACTIVE = "ACTIVE";
const FC_NONE = "NONE";

class HCThemeProvider {
  constructor() {
    this.listeners = [];
    this.forcedColorState = FC_NONE;
    this.colorPallete = null;
    this.node = null;
    this.timer = undefined;
  }

  // in init, registering with mediaQueryProvider. So, init() should be called first before accessing
  // any api of HCThemeProvider
  init = async () => {
    if (!this.mediaQueryProvider) {
      this.mediaQueryProvider = await MediaQueryProvider.getInstance();
      this.mediaQueryProvider.addChangeListener(this.__onForcedColorChange, MEDIA_QUERY_FORCED_COLORS);
      const currState = this.mediaQueryProvider.getBreakpoint(MEDIA_QUERY_FORCED_COLORS);
      this.__onForcedColorChange(currState);
    }
  };

  reset = () => {
    if (this.mediaQueryProvider) {
      this.mediaQueryProvider.removeChangeListener(this.__onForcedColorChange, MEDIA_QUERY_FORCED_COLORS);
    }
    this.mediaQueryProvider = null;
    this.listeners = [];
    this.forcedColorState = FC_NONE;
    this.colorPallete = null;
    this.__destroyDomNode();
    this.__stopPolling();
  };

  addChangeListener = callback => {
    this.listeners.push(callback);
  };

  removeChangeListener = callback => {
    if (this.listeners) {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    }
  };

  getColorPallete = () => this.colorPallete;

  __createDomNode = () => {
    // styling DOM nodes using system-color keywords for high contrast theme colors,
    // storing different theme colors as different css color properties of DOM nodes,
    // with high contrast theme change, system-color keywords value changes, which we monitor
    // ensure to use appropriate contrast colors in background-foreground
    let ele1 = document.createElement("div");
    ele1.style.cssText =
      "color:CanvasText;background-color:Canvas;border:solid;border-left-color:LinkText;border-right-color:GrayText;aria-hidden:true;display:none";

    let ele2 = document.createElement("div");
    ele2.style.cssText = "color:HighlightText;background-color:Highlight;aria-hidden:true;display:none";

    let ele3 = document.createElement("div");
    ele3.style.cssText = "color:ButtonText;background-color:ButtonFace;aria-hidden:true;display:none";

    let fmDiv = document.createElement("div");
    fmDiv.style.cssText = "display:none;aria-hidden:true";
    fmDiv.setAttribute("id", "hcThemeDetectionNode");
    fmDiv.appendChild(ele1);
    fmDiv.appendChild(ele2);
    fmDiv.appendChild(ele3);
    document.body.appendChild(fmDiv);

    this.node = fmDiv;
  };

  __destroyDomNode = () => {
    if (this.node) {
      document.body.removeChild(this.node);
      this.node = null;
    }
  };

  __getParsedColor = colorStr =>
    // colorStr format is: rgb(0, 0, 238)
    colorStr
      .substring(4, colorStr.length - 1)
      .split(", ")
      .map(str => Number(str));

  __isRGBColorStr = colorStr => {
    var matchRGBColors = new RegExp(/rgb\((\d{1,3}), (\d{1,3}), (\d{1,3})\)/);
    if (matchRGBColors.test(colorStr)) {
      return true;
    }
    console.log("Invalid color string: ", colorStr);
    return false;
  };

  __getForcedColorPallete = () => {
    const fcStyle1 = getComputedStyle(this.node.childNodes[0]);
    const fcStyle2 = getComputedStyle(this.node.childNodes[1]);
    const fcStyle3 = getComputedStyle(this.node.childNodes[2]);
    // if expected rgb color string not present, log that case and set theme as null
    if (!this.__isRGBColorStr(fcStyle1.color)) {
      console.log("Unexpected color string format found");
      return null;
    }
    const forcedColorPallete = {
      [hcThemeColors.TEXT]: this.__getParsedColor(fcStyle1.color),
      [hcThemeColors.BACKGROUND]: this.__getParsedColor(fcStyle1.backgroundColor),
      [hcThemeColors.LINK_TEXT]: this.__getParsedColor(fcStyle1.borderLeftColor),
      [hcThemeColors.DISABLED_TEXT]: this.__getParsedColor(fcStyle1.borderRightColor),
      [hcThemeColors.HIGHLIGHT]: this.__getParsedColor(fcStyle2.backgroundColor),
      [hcThemeColors.HIGHLIGHT_TEXT]: this.__getParsedColor(fcStyle2.color),
      [hcThemeColors.BUTTON_FACE]: this.__getParsedColor(fcStyle3.backgroundColor),
      [hcThemeColors.BUTTON_TEXT]: this.__getParsedColor(fcStyle3.color),
      colorSpace: "rgb"
    };
    return forcedColorPallete;
  };

  __callbackListeners = colorPallete => {
    if (this.listeners) {
      this.listeners.forEach(listener => {
        if (listener && typeof listener === "function") {
          listener(colorPallete);
        }
      });
    }
  };

  __onForcedColorChange = newState => {
    this.forcedColorState = newState;
    if (newState === FC_ACTIVE) {
      this.__createDomNode();
      this.colorPallete = this.__getForcedColorPallete();
      trackHCEnabled(this.colorPallete);
      this.__callbackListeners(this.colorPallete);
      this.__startPolling();
    } else {
      trackHCDisabled(this.colorPallete);
      this.colorPallete = null;
      this.__callbackListeners(this.colorPallete);
      this.__destroyDomNode();
      this.__stopPolling();
    }
  };

  __startPolling = () => {
    this.timer = setInterval(this.__detectThemeChange, HC_THEME_POLLING_INTERVAL);
  };

  __stopPolling = () => {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  };

  __detectThemeChange = () => {
    const newColorPallete = this.__getForcedColorPallete();
    if (JSON.stringify(this.colorPallete) !== JSON.stringify(newColorPallete)) {
      trackHCThemeChange(this.colorPallete, newColorPallete);
      this.colorPallete = newColorPallete;
      this.__callbackListeners(this.colorPallete);
    }
  };
}
export default new HCThemeProvider();
